import { Agent, AgentSettings, ExecutionLog, AgentStats, TestResult } from '../../types/agent-types-new';
import { AgentLog, AgentTestInput, AgentTestResult, AgentRating } from '../../types/agent-types';
import { VoiceSettings } from '../../components/agents/voice-types';
import { agentApiFixed } from './agent-api-fixed';
import { agentMappings, getImplementedAgentIds, getComingSoonAgentIds } from './agent-mappings';
import { crewAgents } from '../../data/crew-agents';
import { API_URL, API_BASE_URL as CONFIG_API_BASE_URL } from '../../config/api';
import { authFetch } from '../../utils/auth-fetch';
import { AGENT_ORDER, MISSING_AGENTS, AGENT_ID_MAPPING } from './agent-order-config';

const API_BASE_URL = CONFIG_API_BASE_URL;
// Ensure the image path works in both development and Docker environments
const IMAGE_BASE_PATH = '/crew-agents/';

// For debugging - log API requests
const debugMode = true;
const debugLog = (message: string, data?: any) => {
  if (debugMode) {
    console.log(`[AgentService] ${message}`, data || '');
  }
};

/**
 * Maps agent types from the backend to the new agent categories
 * Include marketplace status: 'available', 'coming-soon', or 'hidden'
 * Based on Meet the Crew list and implemented backend services
 */
const agentTypeToCategory: Record<string, {
  category: Agent['category'];
  color: Agent['color'];
  maturity: Agent['maturity'];
  status: 'available' | 'coming-soon' | 'hidden';
  displayName?: string;
}> = {
  // IMPLEMENTED BACKEND SERVICES - MAP TO AVAILABLE AGENTS
  
  // Page role (not directly in Meet the Crew)
  'page-role': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Semantic SEO Optimizer'
  },
  
  // Page Semantics Learner
  'page-semantic-intent': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'VP',
    status: 'available',
    displayName: 'Page Semantics Learner'
  },
  
  // Customer Intent Learner
  'customer-intent': { 
    category: 'Intent', 
    color: 'purple',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Customer Intent Learner'
  },
  
  // Suggestions with Answer
  'question-suggestion': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Suggestions with Answer'
  },
  
  // Adjacent Topics Explorer
  'adjacent-topics': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'VP',
    status: 'available',
    displayName: 'Adjacent Topics Explorer'
  },
  
  // Competitors Analyst
  'competitive-analysis': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Competitors Analyst'
  },
  
  // ChatBot
  'page-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Senior',
    status: 'available',
    displayName: 'ChatBot'
  },
  
  // Site Crawler
  'site-crawler': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'VP',
    status: 'available',
    displayName: 'Site Crawler'
  },
  
  // VoiceBot
  'voice-assistant': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Senior',
    status: 'available',
    displayName: 'VoiceBot'
  },
  
  // Voice Chatbot (separate implementation)
  'voice-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'VP',
    status: 'available',
    displayName: 'Voice Chatbot'
  },
  
  // Page Narrator
  'narrative-presentation': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Page Narrator'
  },
  
  // Benchmark Analyst
  'auto-evaluator': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Benchmark Analyst'
  },
  
  // Content Recommender
  'intent-based-recommender': { 
    category: 'Intent', 
    color: 'purple',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Content Recommender'
  },
  
  // Style Suggestion (not directly in Meet the Crew)
  'style-suggestion': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Style Suggestion'
  },
  
  // COMING SOON AGENTS - NOT IMPLEMENTED YET
  
  // Web Analyst
  'web-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Web Analyst'
  },
  
  // Counter Narrative Builder
  'counter-narrative': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Counter Narrative Builder'
  },
  
  // Hands-off Navigation
  'hands-off-navigation': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Hands-off Navigation'
  },
  
  // Bug Finder
  'bug-finder': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'Rookie',
    status: 'coming-soon',
    displayName: 'Bug Finder'
  },
  
  // Topic Research Agent
  'topic-research': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Topic Research Agent'
  },
  
  // MISSING AGENTS - NOW AVAILABLE
  'product-comparison': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Product Comparison Agent'
  },
  'customer-surveys': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Know Your Customer Surveys Agent'
  },
  'replay-analyst': { 
    category: 'Usability', 
    color: 'red',
    maturity: 'VP',
    status: 'available',
    displayName: 'Replay Analyst: Friction Finder'
  },
  'behavior-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Behavior Analyst'
  },
  'sector-assigner': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Sector Assigner'
  },
  
  // NEW AGENTS
  'adjacent-topics-suggestor': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Adjacent Topics Suggestor'
  },
  'growth-analyst': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Growth Analyst'
  },
  'segment-research': { 
    category: 'Research', 
    color: 'yellow',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Segment Research Agent'
  },
  'customer-segmentation': { 
    category: 'Analytics', 
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Customer Segmentation Agent'
  },
  'sector-topic-generator': { 
    category: 'Content', 
    color: 'blue',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Sector Topic Generator'
  },
  'site-sector-classifier': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Site Sector Classifier'
  },
  'site-sector-analyzer': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Site Sector Analyzer'
  },
  
  // CHATBOT ANALYTICS AGENTS
  'chatbot-intent-classifier': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Intent Classifier'
  },
  'chatbot-quality-scorer': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Quality Scorer'
  },
  'chatbot-content-gap-detector': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Chatbot Content Gap Detector'
  },
  'chatbot-conversation-flow-analyzer': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'Senior',
    status: 'available',
    displayName: 'Chatbot Conversation Flow Analyzer'
  },
  'chatbot-analytics-agent': {
    category: 'Analytics',
    color: 'orange',
    maturity: 'VP',
    status: 'available',
    displayName: 'Chatbot Analytics Orchestrator'
  },
  
  // HIDDEN AGENTS - DEV/TEST/INACTIVE
  'mock-page-chatbot': { 
    category: 'Support', 
    color: 'teal',
    maturity: 'Rookie',
    status: 'hidden'
  },
  'keyword-extractor': { 
    category: 'SEO', 
    color: 'green',
    maturity: 'Rookie',
    status: 'available',
    displayName: 'Keyword Extractor Agent'
  }
};

/**
 * Maps agent types to avatar and action video paths
 * Uses the Meet the Crew image paths for consistent naming
 */
const agentTypeToMedia: Record<string, {
  avatar: string;
  actionVideo: string;
}> = {
  // IMPLEMENTED AGENTS (Available)
  'page-role': { 
    avatar: `${IMAGE_BASE_PATH}page-role.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}page-role-action.mp4`
  },
  'page-semantic-intent': { 
    avatar: `${IMAGE_BASE_PATH}page-semantic.png`,
    actionVideo: `${IMAGE_BASE_PATH}page-semantic-action.mp4`
  },
  'customer-intent': { 
    avatar: `${IMAGE_BASE_PATH}customer-intent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-intent-action.mp4`
  },
  'question-suggestion': { 
    avatar: `${IMAGE_BASE_PATH}suggestions-with-answers.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}suggestions-with-answers-action.mp4`
  },
  'adjacent-topics': { 
    avatar: `${IMAGE_BASE_PATH}adjacent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-action.mp4`
  },
  'auto-evaluator': { 
    avatar: `${IMAGE_BASE_PATH}auto-evaluator.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}auto-evaluator-action.mp4`
  },
  'competitive-analysis': { 
    avatar: `${IMAGE_BASE_PATH}competitors.png`,
    actionVideo: `${IMAGE_BASE_PATH}competitors-action.mp4`
  },
  'intent-based-recommender': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'page-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'narrative-presentation': { 
    avatar: `${IMAGE_BASE_PATH}narrator.png`, 
    actionVideo: `${IMAGE_BASE_PATH}narrator-action.mp4`
  },
  'site-crawler': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  'site-crawler-agent': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  'style-suggestion': { 
    avatar: `${IMAGE_BASE_PATH}style-suggestion.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}style-suggestion-action.mp4`
  },
  'voice-assistant': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  'voice-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  
  // Mappings for agents with -agent suffix in database
  'page-role-agent': { 
    avatar: `${IMAGE_BASE_PATH}page-role.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}page-role-action.mp4`
  },
  'page-semantic-intent-agent': { 
    avatar: `${IMAGE_BASE_PATH}page-semantic.png`,
    actionVideo: `${IMAGE_BASE_PATH}page-semantic-action.mp4`
  },
  'customer-intent-agent': { 
    avatar: `${IMAGE_BASE_PATH}customer-intent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-intent-action.mp4`
  },
  'page-chatbot-agent': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'voice-assistant-agent': { 
    avatar: `${IMAGE_BASE_PATH}voicebot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}voicebot-action.mp4`
  },
  'content-analysis-agent': { 
    avatar: `${IMAGE_BASE_PATH}content-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'analytics-insight-agent': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'marketing-optimization-agent': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'decision-support-agent': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'form-understanding-agent': { 
    avatar: `${IMAGE_BASE_PATH}intent-checker.png`,
    actionVideo: `${IMAGE_BASE_PATH}intent-checker-action.mp4`
  },
  'dynamic-interaction-agent': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'task-execution-agent': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'multi-page-analysis-agent': { 
    avatar: `${IMAGE_BASE_PATH}sector-topic-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-topic-generator-action.mp4`
  },
  
  // Missing agents that were showing ui-avatars
  'orchestration': { 
    avatar: `${IMAGE_BASE_PATH}orchestration.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}orchestration-action.mp4`
  },
  'onboarding': { 
    avatar: `${IMAGE_BASE_PATH}onboarding.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}onboarding-action.mp4`
  },
  'site-sector-analyzer': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  'conductor': { 
    avatar: `${IMAGE_BASE_PATH}conductor-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}conductor-agent-action.mp4`
  },
  'insights-hub': { 
    avatar: `${IMAGE_BASE_PATH}insight-hub.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}insight-hub-action.mp4`
  },
  'session-summarizer': { 
    avatar: `${IMAGE_BASE_PATH}session-summarizer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}session-summarizer-action.mp4`
  },
  'insights-hub-agent': { 
    avatar: `${IMAGE_BASE_PATH}insight-hub.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}insight-hub-action.mp4`
  },
  'session-summarizer-agent': { 
    avatar: `${IMAGE_BASE_PATH}session-summarizer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}session-summarizer-action.mp4`
  },
  'orchestration-agent': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  'topic-research': { 
    avatar: `${IMAGE_BASE_PATH}topic-research.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}topic-research-action.mp4`
  },
  
  // MISSING AGENTS - Need images/videos
  'product-comparison': { 
    avatar: `${IMAGE_BASE_PATH}product-comparison.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}product-comparison-action.mp4`
  },
  'customer-surveys': { 
    avatar: `${IMAGE_BASE_PATH}kyc-survey.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}kyc-survey-action.mp4`
  },
  'replay-analyst': { 
    avatar: `${IMAGE_BASE_PATH}replay-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}replay-analyst-action.mp4`
  },
  'behavior-analyst': { 
    avatar: `${IMAGE_BASE_PATH}behavior-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}behavior-analyst-action.mp4`
  },
  'sector-assigner': { 
    avatar: `${IMAGE_BASE_PATH}sector-assigner.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-assigner-action.mp4`
  },
  
  // NEW AGENT MEDIA
  'adjacent-topics-suggestor': { 
    avatar: `${IMAGE_BASE_PATH}adjacent-topics-suggestor.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-suggestor-action.mp4`
  },
  'growth-analyst': { 
    avatar: `${IMAGE_BASE_PATH}growth-analyst.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}growth-analyst-action.mp4`
  },
  'segment-research': { 
    avatar: `${IMAGE_BASE_PATH}segment-research-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}segment-research-agent-action.mp4`
  },
  'customer-segmentation': { 
    avatar: `${IMAGE_BASE_PATH}customer-segmentation-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}customer-segmentation-agent-action.mp4`
  },
  'sector-topic-generator': { 
    avatar: `${IMAGE_BASE_PATH}sector-topic-generator.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}sector-topic-generator-action.mp4`
  },
  'site-sector-classifier': { 
    avatar: `${IMAGE_BASE_PATH}site-sector-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}site-sector-classifier-action.mp4`
  },
  
  // Additional mappings for agents in the database
  'usability-insight': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'seo-performance': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'lead-capture': { 
    avatar: `${IMAGE_BASE_PATH}suggestions-with-answers.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}suggestions-with-answers-action.mp4`
  },
  'experiment-analyst': { 
    avatar: `${IMAGE_BASE_PATH}benchmark.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}benchmark-action.mp4`
  },
  'automatic-survey': { 
    avatar: `${IMAGE_BASE_PATH}narrator.png`,
    actionVideo: `${IMAGE_BASE_PATH}narrator-action.mp4`
  },
  'autonomous-layout': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'intent': { 
    avatar: `${IMAGE_BASE_PATH}adjacent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}adjacent-topics-action.mp4`
  },
  'pricing-optimization': { 
    avatar: `${IMAGE_BASE_PATH}content-recommender.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}content-generator-action.mp4`
  },
  'semantic-parsing': { 
    avatar: `${IMAGE_BASE_PATH}crawler.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}crawler-action.mp4`
  },
  
  // COMING SOON AGENTS
  'web-analyst': { 
    avatar: `${IMAGE_BASE_PATH}webanalytics.png`,
    actionVideo: `${IMAGE_BASE_PATH}webanalytics-action.mp4`
  },
  'counter-narrative': { 
    avatar: `${IMAGE_BASE_PATH}counter-narrative.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}counter-narrative-action.mp4`
  },
  'hands-off-navigation': { 
    avatar: `${IMAGE_BASE_PATH}handsoff.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}handsoff-action.mp4`
  },
  'bug-finder': { 
    avatar: `${IMAGE_BASE_PATH}intent-checker.png`,
    actionVideo: `${IMAGE_BASE_PATH}intent-checker-action.mp4`
  },
  
  // HIDDEN AGENTS
  'keyword-extractor': { 
    avatar: `${IMAGE_BASE_PATH}keyword-extractor.jpg`, // Use the correct image from Meet the Crew
    actionVideo: `${IMAGE_BASE_PATH}keyword-extractor-action.mp4`
  },
  'mock-page-chatbot': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  'chatbot-voice': { 
    avatar: `${IMAGE_BASE_PATH}chatbot.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-action.mp4`
  },
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': {
    avatar: `${IMAGE_BASE_PATH}chatbot-intent-classifier.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-intent-classifier-action.mp4`
  },
  'chatbot-quality-scorer': {
    avatar: `${IMAGE_BASE_PATH}chatbot-quality-scorer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-quality-scorer-action.mp4`
  },
  'chatbot-content-gap-detector': {
    avatar: `${IMAGE_BASE_PATH}chatbot-content-gap-detector.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-content-gap-detector-action.mp4`
  },
  'chatbot-conversation-flow-analyzer': {
    avatar: `${IMAGE_BASE_PATH}chatbot-conversation-flow-analyzer.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-conversation-flow-analyzer-action.mp4`
  },
  'chatbot-analytics-agent': {
    avatar: `${IMAGE_BASE_PATH}chatbot-analytics-agent.jpg`,
    actionVideo: `${IMAGE_BASE_PATH}chatbot-analytics-agent-action.mp4`
  }
};

/**
 * Maps agent types to descriptive missions
 * Uses exact descriptions from Meet the Crew where available
 */
const agentTypeToMission: Record<string, string> = {
  // IMPLEMENTED AGENTS
  'page-role': 'Analyzes site structure to optimize semantic organization and SEO performance',
  'page-semantic-intent': 'Understands the structure, context, and meaning of page content',
  'customer-intent': 'Analyzes user behavior to understand purchase intent and motivations',
  'question-suggestion': 'Provides proactive suggestions with ready-to-use answers for common questions',
  'adjacent-topics': 'Identifies related topics to expand content reach and relevance',
  'auto-evaluator': 'Measures your website\'s performance against industry benchmarks',
  'competitive-analysis': 'Tracks competitor websites and extracts comparative insights',
  'intent-based-recommender': 'Suggests the most relevant content to users based on their profile and journey',
  'page-chatbot': 'Engages users in conversational interactions to resolve queries and guide navigation',
  'narrative-presentation': 'Reads aloud page content to improve accessibility and engagement',
  'site-crawler': 'Scans the entire website to collect and update structured content',
  'style-suggestion': 'Analyzes website styles and suggests compatible design tokens',
  'voice-assistant': 'Provides voice-based interactions for site navigation and customer assistance',
  'voice-chatbot': 'Conducts natural voice conversations with personalized responses and context awareness',
  
  // HIDDEN AGENTS
  'keyword-extractor': 'Extracts key terms and phrases from content',
  'mock-page-chatbot': 'Simulates conversational interactions for web pages',
  
  // COMING SOON AGENTS
  'web-analyst': 'Monitors website traffic, user behavior, and engagement patterns to provide actionable insights',
  'counter-narrative': 'Detects misinformation and builds counter-narratives to protect brand reputation',
  'hands-off-navigation': 'Guides users through websites automatically without manual clicks',
  'bug-finder': 'Identifies usability issues and broken elements on your site',
  'topic-research': 'Conducts in-depth research on specific topics to generate comprehensive content insights',
  'product-comparison': 'Analyzes and compares products across your site to provide intelligent recommendations',
  'customer-surveys': 'Designs intelligent surveys to understand customer needs and preferences',
  'replay-analyst': 'Reviews user session replays to identify pain points and friction in the user journey',
  'behavior-analyst': 'Studies user behavior patterns to provide actionable insights for optimization',
  'sector-assigner': 'Analyzes site content to automatically assign appropriate business sectors',
  'adjacent-topics-suggestor': 'Suggests related topics to customers to enhance their journey and discovery',
  'growth-analyst': 'Analyzes growth metrics and provides insights to scale your business',
  'segment-research': 'Performs in-depth research on identified customer segments to uncover insights',
  'customer-segmentation': 'Transforms your customer base into actionable segments with AI-powered analysis',
  'sector-topic-generator': 'Generates relevant topics based on business sector analysis',
  'site-sector-classifier': 'Analyzes site content to automatically assign appropriate business sectors',
  'site-sector-analyzer': 'Analyzes site content to automatically assign appropriate business sectors',
  'insights-hub': 'Central hub that aggregates and synthesizes insights from all active agents',
  'session-summarizer': 'Analyzes completed chat conversations to create actionable summaries',
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': 'Analyze chatbot messages using AI to classify user intents with semantic understanding',
  'chatbot-quality-scorer': 'Evaluate chatbot response quality using AI for relevance, helpfulness, and accuracy',
  'chatbot-content-gap-detector': 'Identify missing content and knowledge gaps in chatbot interactions',
  'chatbot-conversation-flow-analyzer': 'Map and analyze chatbot conversation patterns to optimize user journeys',
  'chatbot-analytics-agent': 'Orchestrate the entire chatbot analytics pipeline for comprehensive insights'
};

/**
 * Maps agent types to current development challenges
 * Based on the 'Care and Maintenance' and 'Potential Evolutions' from Meet the Crew
 */
const agentTypeToChallenge: Record<string, string> = {
  // IMPLEMENTED AGENTS
  'page-role': 'Improve accuracy in distinguishing similar page types',
  'page-semantic-intent': 'Needs periodic retraining as site content evolves',
  'customer-intent': 'Needs regular input from web analytics and CRM',
  'question-suggestion': 'Needs regular update of answer database',
  'adjacent-topics': 'Requires monitoring of industry trends',
  'auto-evaluator': 'Needs updated benchmark datasets',
  'competitive-analysis': 'Needs competitor monitoring schedules',
  'intent-based-recommender': 'Needs access to user behavior data and content metadata',
  'page-chatbot': 'Requires training data updates and conversation design',
  'narrative-presentation': 'Needs updated text-to-speech content synchronization',
  'site-crawler': 'Requires crawl scheduling and exclusion rules',
  'style-suggestion': 'Improve accuracy in extracting design patterns',
  'voice-assistant': 'Needs voice command training and TTS tuning',
  'voice-chatbot': 'Needs integration with page context and conversation history management',
  
  // HIDDEN AGENTS
  'keyword-extractor': 'Better handling of industry-specific terminology',
  'mock-page-chatbot': 'Improve natural conversational flow',
  
  // COMING SOON
  'web-analyst': 'Requires integration with web analytics tools and continuous data refresh',
  'counter-narrative': 'Requires real-time monitoring of external content',
  'hands-off-navigation': 'Requires continuous mapping of site structure',
  'bug-finder': 'Requires regular site scans',
  'topic-research': 'Maintaining topic relevance and research quality across diverse industries',
  'product-comparison': 'Building comprehensive comparison frameworks',
  'customer-surveys': 'Creating contextual and non-intrusive survey experiences',
  'replay-analyst': 'Processing large volumes of session data efficiently',
  'behavior-analyst': 'Identifying meaningful patterns from complex behavioral data',
  'sector-assigner': 'Accurate sector classification across diverse industries',
  'adjacent-topics-suggestor': 'Balancing relevance with discovery for optimal user engagement',
  'growth-analyst': 'Providing actionable insights from complex growth metrics',
  'segment-research': 'Requires regular updates to research methodologies and continuous segment data analysis',
  'customer-segmentation': 'Requires regular customer data updates and segmentation model refinement',
  'sector-topic-generator': 'Maintaining topic quality and relevance across different sectors',
  'site-sector-classifier': 'Requires sector taxonomy updates',
  'site-sector-analyzer': 'Requires sector taxonomy updates',
  'insights-hub': 'Requires regular synchronization with all agent outputs',
  'session-summarizer': 'Monitor OpenAI API usage, ensure timely processing of chat sessions',
  
  // Chatbot Analytics Agents
  'chatbot-intent-classifier': 'Improve multi-intent detection and industry-specific models',
  'chatbot-quality-scorer': 'Develop domain-specific quality models and sentiment-aware scoring',
  'chatbot-content-gap-detector': 'Automate content generation for identified gaps',
  'chatbot-conversation-flow-analyzer': 'Implement real-time flow guidance and personalized paths',
  'chatbot-analytics-agent': 'Enable real-time stream processing and self-healing pipelines'
};

/**
 * Create a mock agent from just an agent type
 * Extracted as a standalone function for direct use
 */
const createMockAgentFromType = (agentType: string): Agent => {
  // Use the mapping info for this agent type
  const mappingInfo = agentTypeToCategory[agentType] || {
    category: 'Research',
    color: 'blue',
    maturity: 'Rookie',
    status: 'available'
  };
  
  // Get media info (avatar & videos)
  const mediaInfo = agentTypeToMedia[agentType] || {
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(agentType)}&background=random`,
    actionVideo: ''
  };
  
  // For coming soon agents, show lower progress
  const progress = mappingInfo.status === 'coming-soon' 
    ? Math.floor(Math.random() * 30) + 10 // 10-40% progress for coming soon
    : Math.floor(Math.random() * 60) + 20; // 20-80% progress for available
  
  // Format agent name as capitalized words
  const formatAgentName = (type: string): string => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Generate mock performance data for graphs
  const generateMockPerformanceData = (): number[] => {
    const data = [];
    let value = Math.floor(Math.random() * 30) + 30; // Start between 30-60%
    
    for (let i = 0; i < 12; i++) {
      value += Math.floor(Math.random() * 6) - 1; // Add between -1 and +5
      value = Math.max(25, Math.min(95, value)); // Keep between 25% and 95%
      data.push(value);
    }
    
    return data;
  };
  
  // For voice agents, we need to ensure they have different names and descriptions
  let agentName = mappingInfo.displayName || formatAgentName(agentType);
  let agentMission = agentTypeToMission[agentType] || `Agent for ${agentType}`;
  
  // Special handling for voice agents to ensure distinct identities
  if (agentType === 'voice-chatbot') {
    agentName = 'Voice Chatbot';
    agentMission = 'Conducts natural voice conversations with personalized responses and context awareness';
    debugLog('Creating Voice Chatbot with distinct identity');
  } else if (agentType === 'voice-assistant') {
    agentName = 'VoiceBot';
    agentMission = 'Provides voice-based interactions for site navigation and customer assistance';
    debugLog('Creating VoiceBot with distinct identity');
  }
  
  return {
    _id: agentType,
    agent_type: agentType,
    name: agentName,
    avatar: mediaInfo.avatar,
    actionVideo: mediaInfo.actionVideo,
    mission: agentMission,
    maturity: mappingInfo.maturity,
    progress: progress,
    challenge: agentTypeToChallenge[agentType] || 'Improving accuracy and performance',
    category: mappingInfo.category,
    color: mappingInfo.color,
    status: mappingInfo.status === 'coming-soon' ? 'not-hired' : 'active',
    performanceData: generateMockPerformanceData(),
    performanceChange: Math.floor(Math.random() * 20) + 5,
    findingsCount: Math.floor(Math.random() * 40) + 5,
    createdAt: new Date().toISOString(),
    lastActive: new Date().toISOString(),
    source: 'PageMind',
    marketplaceStatus: mappingInfo.status
  };
};

/**
 * Service for interacting with agent data
 * Maps between the legacy backend API and our new agent model
 */
export { createMockAgentFromType };

export const agentService = {
  /**
   * Create a mock agent from just an agent type
   * Available as a method for direct use
   */
  createMockAgentFromType,
  
  /**
   * Determine the standard agent type from an agent object
   * Maps MongoDB agent objects to standard agent types 
   * Exported for use in other components
   */
  determineAgentType(agent: any): string {
    // If agent has an explicit agent_type field, use that
    if (agent.agent_type) {
      return agent.agent_type;
    }

    // If agent has a name field, try to map it to a standard type
    if (agent.name) {
      const name = agent.name.toLowerCase();
      
      // Map common names to agent types
      if (name.includes('page semantic') || name.includes('semantic')) {
        return 'page-semantic-intent';
      }
      
      if (name.includes('page role') || name.includes('role')) {
        return 'page-role';
      }
      
      if (name.includes('customer intent') || name.includes('intent')) {
        return 'customer-intent';
      }
      
      if (name.includes('chatbot') || name.includes('chat')) {
        return 'page-chatbot';
      }
      
      if (name.includes('voice')) {
        return 'voice-assistant';
      }
    }
    
    // Use the category field to determine a likely agent type
    if (agent.category) {
      switch (agent.category.toLowerCase()) {
        case 'seo':
          return 'page-semantic-intent';
        case 'content':
          return 'adjacent-topics';
        case 'intent':
          return 'customer-intent';
        case 'support':
          return 'page-chatbot';
        case 'research':
          return 'competitive-analysis';
        case 'analytics':
          return 'auto-evaluator';
      }
    }
    
    // If we couldn't determine the type, return page-role as a default
    return 'page-role';
  },
  /**
   * Check if agent has voice capabilities
   */
  hasVoiceCapabilities(agent: Agent | null | undefined): boolean {
    if (!agent) return false;
    return agent._id === 'voice-assistant' || agent._id === 'voice-chatbot';
  },
  
  /**
   * Get voice settings for an agent
   */
  async getVoiceSettings(agentId: string): Promise<VoiceSettings> {
    debugLog(`Fetching voice settings for agent ${agentId}`);
    try {
      // In a real implementation, we would fetch from the API
      // For now, return default settings
      return {
        voiceProvider: 'openai',
        voice: 'nova',
        language: 'en',
        model: 'gpt-4o',
      };
    } catch (error) {
      console.error(`Error fetching voice settings for agent ${agentId}:`, error);
      
      // Return default settings
      return {
        voiceProvider: 'openai',
        voice: 'nova',
        language: 'en',
        model: 'gpt-4o',
      };
    }
  },
  
  /**
   * Update voice settings
   */
  async updateVoiceSettings(agentId: string, settings: VoiceSettings): Promise<boolean> {
    debugLog(`Updating voice settings for agent ${agentId}`, settings);
    try {
      // In a real implementation, we would save to the API
      // For now, just return success
      return true;
    } catch (error) {
      console.error(`Error updating voice settings for agent ${agentId}:`, error);
      return false;
    }
  },
  
  /**
   * Get WebSocket URL for voice assistant
   */
  async getVoiceAssistantWsUrl(): Promise<string> {
    try {
      // Fetch from API
      const response = await fetch(`${API_BASE_URL}/voice-assistant/ws-url`);
      if (response.ok) {
        const data = await response.json();
        return data.url;
      }
    } catch (error) {
      console.error('Error fetching WebSocket URL:', error);
    }
    
    // Fallback to default if API call fails - use dynamic URL based on current host
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/voice-assistant`;
  },
  
  /**
   * Get voice assistant service status
   */
  async getVoiceAssistantStatus(): Promise<{ status: 'online' | 'offline' | 'degraded', message: string }> {
    try {
      // Fetch from API
      const response = await fetch(`${API_BASE_URL}/voice-assistant/status`);
      if (response.ok) {
        const data = await response.json();
        return {
          status: data.status,
          message: data.message
        };
      }
    } catch (error) {
      console.error('Error fetching voice assistant status:', error);
    }
    
    // Fallback if API call fails
    return {
      status: 'offline',
      message: 'Could not connect to voice assistant service'
    };
  },
  /**
   * Fetch all agents
   * @param includeComingSoon Whether to include "coming soon" agents
   * @param includeHidden Whether to include hidden agents
   */
  async getAgents(includeComingSoon: boolean = true, includeHidden: boolean = false): Promise<Agent[]> {
    debugLog('Fetching all agents');
    console.log('[DEBUG] getAgents called with:', { includeComingSoon, includeHidden });
    
    try {
      // First, try to fetch from the agents API endpoint
      const response = await authFetch(`${API_BASE_URL}/agents`);
      
      let agents: Agent[] = [];
      
      if (response.ok) {
        const data = await response.json();
        debugLog('Successfully fetched agents from API');
        console.log('[DEBUG] Raw API response:', data);
        
        if (data.agents && Array.isArray(data.agents)) {
          // Map to our new Agent model
          agents = data.agents.map((agent: any) => this.mapToNewAgentModel(agent));
          console.log('[DEBUG] Mapped agents from API:', agents.map(a => ({ id: a._id, name: a.name })));
        }
      } else {
        console.log('[DEBUG] API response not OK:', response.status);
      }
      
      // If the API fails or returns no agents, fall back to creating from our ordered list
      if (agents.length === 0) {
        debugLog('API failed or returned no agents, creating from ordered list');
        console.log('[DEBUG] Creating agents from AGENT_ORDER:', AGENT_ORDER);
        
        // Create agents from our ordered list
        agents = AGENT_ORDER.map(agentType => createMockAgentFromType(agentType));
      }
      
      console.log('[DEBUG] Before normalization:', agents.length, 'agents');
      
      // Normalize agent IDs to handle duplicates
      const normalizedAgents = new Map<string, Agent>();
      agents.forEach(agent => {
        // Apply ID mapping to consolidate duplicates
        const normalizedId = AGENT_ID_MAPPING[agent._id] || agent._id;
        
        // Skip voice-chatbot in favor of voice-assistant
        if (agent._id === 'voice-chatbot' || agent._id === 'voice-chatbot-agent') {
          console.log('[DEBUG] Skipping duplicate voice agent:', agent._id);
          return; // Skip this duplicate
        }
        
        // Only keep the first occurrence of each normalized ID
        if (!normalizedAgents.has(normalizedId)) {
          normalizedAgents.set(normalizedId, { ...agent, _id: normalizedId });
        } else {
          console.log('[DEBUG] Skipping duplicate agent:', agent._id, '->', normalizedId);
        }
      });
      
      // Convert back to array
      agents = Array.from(normalizedAgents.values());
      console.log('[DEBUG] After normalization:', agents.length, 'agents');
      
      // Filter based on status
      const filteredAgents = agents.filter(agent => {
        const agentInfo = agentTypeToCategory[agent._id];
        if (!agentInfo) {
          console.log('[DEBUG] No category info for agent:', agent._id);
          return true; // Include if not in our mapping
        }
        
        if (agentInfo.status === 'hidden' && !includeHidden) {
          console.log('[DEBUG] Filtering out hidden agent:', agent._id);
          return false;
        }
        if (agentInfo.status === 'coming-soon' && !includeComingSoon) {
          console.log('[DEBUG] Filtering out coming-soon agent:', agent._id);
          return false;
        }
        
        return true;
      });
      
      console.log('[DEBUG] After filtering:', filteredAgents.length, 'agents');
      
      // Sort agents according to AGENT_ORDER
      const sortedAgents = filteredAgents.sort((a, b) => {
        const indexA = AGENT_ORDER.indexOf(a._id);
        const indexB = AGENT_ORDER.indexOf(b._id);
        
        // If not in order list, put at end
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        
        return indexA - indexB;
      });
      
      console.log('[DEBUG] Final sorted agents:', sortedAgents.map(a => ({ 
        id: a._id, 
        name: a.name, 
        orderIndex: AGENT_ORDER.indexOf(a._id) 
      })));
      
      return sortedAgents;
    } catch (error) {
      console.error('Error fetching agents:', error);
      
      // Return an empty array if all else fails
      return [];
    }
  },
  
  /**
   * Fetch a specific agent by ID
   */
  async getAgentById(id: string): Promise<Agent | null> {
    debugLog(`Fetching agent ${id}`);
    try {
      // First try the regular API path
      try {
        // Check if this ID is likely a MongoDB ObjectId (24 hex chars)
        const isLikelyMongoId = /^[0-9a-f]{24}$/i.test(id);
        
        if (isLikelyMongoId) {
          // Try to fetch from the MongoDB API
          try {
            const response = await fetch(`${API_BASE_URL}/agents/${id}`);
            
            if (response.ok) {
              const data = await response.json();
              debugLog('Successfully fetched agent from API');
              return this.mapToNewAgentModel(data.agent);
            } else {
              debugLog(`API returned error status ${response.status} for MongoDB ID ${id}`);
            }
          } catch (fetchError) {
            debugLog(`Error fetching MongoDB agent ${id}: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`);
          }
        } else {
          // This is likely a standard agent type like 'page-role', not a MongoDB ID
          debugLog(`ID ${id} appears to be a standard agent type, not a MongoDB ID. Skipping direct API call.`);
        }
        
        // If the API fails or ID is not a MongoDB ID, fetch all agents and find the matching one
        debugLog('Trying to find agent in all available agents');
        const allAgents = await this.getAgents(true, true); // Include all agent types
        const foundAgent = allAgents.find(agent => 
          agent._id === id || 
          agent.agent_type === id || 
          this.determineAgentType(agent) === id
        );
        
        if (foundAgent) {
          return foundAgent;
        }
      } catch (mainPathError) {
        debugLog(`Main path for fetching agent failed: ${mainPathError.message}`);
        // Continue to fallbacks
      }
      
      // Try to use our bypass endpoint as a guaranteed fallback
      try {
        debugLog(`Attempting bypass endpoint for agent: ${id}`);
        const bypassResponse = await fetch(`${API_BASE_URL}/bypass/agent/${id}`);
        
        if (bypassResponse.ok) {
          const bypassData = await bypassResponse.json();
          if (bypassData && bypassData.agent) {
            debugLog('Successfully got mock agent from bypass endpoint');
            return {
              ...bypassData.agent,
              isMock: true 
            } as Agent;
          }
        }
      } catch (bypassError) {
        debugLog(`Bypass endpoint failed: ${bypassError.message}`);
      }
      
      // Final fallback - create a mock agent
      // But first, check if we need to determine the agent type from the fetched data
      debugLog(`Creating mock agent from agent type: ${id}`);
      
      // If the ID looks like a UUID and not a standard agent type, we can't create a proper mock
      if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
        debugLog(`ID ${id} is a UUID, creating a generic mock agent`);
        return {
          _id: id,
          name: 'Unknown Agent',
          avatar: `https://ui-avatars.com/api/?name=Unknown+Agent&background=random`,
          actionVideo: '',
          mission: 'Agent information not available',
          maturity: 'Rookie',
          progress: 0,
          challenge: 'Unable to fetch agent details',
          category: 'Research',
          color: 'blue',
          status: 'not-hired',
          performanceData: [],
          performanceChange: 0,
          findingsCount: 0,
          createdAt: new Date().toISOString(),
          lastActive: new Date().toISOString(),
          source: 'PageMind',
          marketplaceStatus: 'hidden'
        };
      }
      
      return createMockAgentFromType(id);
    } catch (error) {
      console.error(`Error fetching agent ${id}:`, error);
      // Create a fallback mock agent 
      return createMockAgentFromType(id);
    }
  },
  
  /**
   * Fetch an agent by type (agent_type)
   */
  async getAgentByType(agentType: string): Promise<Agent | null> {
    debugLog(`Fetching agent by type ${agentType}`);
    try {
      // Fetch all agents and find the one with matching type
      const allAgents = await this.getAgents();
      return allAgents.find(agent => agent._id === agentType) || 
             createMockAgentFromType(agentType);
    } catch (error) {
      console.error(`Error fetching agent by type ${agentType}:`, error);
      return null;
    }
  },
  
  /**
   * Update an agent's status (active/paused)
   */
  async updateAgentStatus(agentId: string, status: 'active' | 'paused'): Promise<boolean> {
    debugLog(`Updating agent ${agentId} status to ${status}`);
    try {
      // Check if this ID is likely a MongoDB ObjectId (24 hex chars)
      const isLikelyMongoId = /^[0-9a-f]{24}$/i.test(agentId);
      
      if (!isLikelyMongoId) {
        // If not a MongoDB ID, try to find the agent to get its MongoDB ID
        debugLog(`Agent ID ${agentId} is not a MongoDB ID, trying to find the actual agent`);
        const agent = await this.getAgentById(agentId);
        
        if (agent && agent._id && /^[0-9a-f]{24}$/i.test(agent._id)) {
          // Use the MongoDB ID from the found agent
          agentId = agent._id;
          debugLog(`Found MongoDB ID ${agentId} for agent type ${agentId}`);
        } else {
          debugLog(`Could not find a valid MongoDB ID for agent ${agentId}`);
          return false; // Cannot update without a valid MongoDB ID
        }
      }
      
      const endpoint = status === 'active' ? 'hire' : 'fire';
      const response = await fetch(`${API_BASE_URL}/agents/${agentId}/${endpoint}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        debugLog(`Update status API call failed with status ${response.status}`);
      }
      
      return response.ok;
    } catch (error) {
      console.error(`Error updating agent ${agentId} status:`, error);
      return false;
    }
  },
  
  /**
   * Get execution logs for a specific agent
   */
  async getAgentLogs(agentType: string, limit: number = 20, offset: number = 0): Promise<ExecutionLog[]> {
    debugLog(`Fetching logs for agent ${agentType}`);
    try {
      // Use the log API from agent-api-fixed
      const logsResult = await agentApiFixed.getLogs({ 
        agentType,
        limit,
        offset
      });
      
      // Map logs to our new ExecutionLog model
      if (logsResult && logsResult.logs && Array.isArray(logsResult.logs)) {
        return logsResult.logs.map((log: AgentLog) => this.mapToExecutionLog(log));
      }
      return [];
    } catch (error) {
      console.error(`Error fetching logs for agent ${agentType}:`, error);
      return [];
    }
  },
  
  /**
   * Get a specific log by ID - OPTIMIZED VERSION
   * Selects the best endpoint based on ID format to improve performance
   */
  async getLogById(logId: string): Promise<ExecutionLog> {
    debugLog(`Fetching log ${logId}`);
    try {
      // Choose the optimal endpoint based on the ID format
      // This avoids trying multiple endpoints in sequence, significantly improving performance
      
      // For trace IDs (non-MongoDB ObjectId format)
      if (logId && (logId.startsWith('trace-') || !/^[0-9a-f]{24}$/i.test(logId))) {
        try {
          debugLog(`Using specialized trace endpoint for: ${logId}`);
          const response = await fetch(`${API_BASE_URL}/logs/by-trace/${logId}`);
          if (response.ok) {
            const data = await response.json();
            debugLog('Successfully fetched log from trace endpoint');
            return this.mapToExecutionLog(data.log);
          }
          // If this fails, fall through to the bypass endpoint
        } catch (error) {
          debugLog(`Trace endpoint error: ${error instanceof Error ? error.message : String(error)}`);
          // Continue to fallback
        }
      } 
      // For MongoDB ObjectId format
      else if (logId && /^[0-9a-f]{24}$/i.test(logId)) {
        try {
          debugLog(`Using standard logs endpoint for ObjectId: ${logId}`);
          const response = await fetch(`${API_BASE_URL}/logs/${logId}`);
          if (response.ok) {
            const data = await response.json();
            debugLog('Successfully fetched log from standard endpoint');
            return this.mapToExecutionLog(data.log);
          }
          // If this fails, fall through to the bypass endpoint
        } catch (error) {
          debugLog(`Standard endpoint error: ${error instanceof Error ? error.message : String(error)}`);
          // Continue to fallback
        }
      }
      
      // Use the bypass endpoint as a guaranteed fallback
      // This always returns mock data without querying the database
      try {
        debugLog(`Using bypass endpoint for guaranteed response: ${logId}`);
        const response = await fetch(`${API_BASE_URL}/bypass/log/${logId}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.log) {
            debugLog('Successfully fetched mock data from bypass endpoint');
            return this.mapToExecutionLog({
              ...data.log,
              traceId: logId,
              isMockData: true
            });
        }
      }
    } catch (bypassError) {
      debugLog(`Bypass endpoint error: ${bypassError instanceof Error ? bypassError.message : String(bypassError)}`);
    } finally {
      // If all endpoints fail, return a simple mock log to avoid crashing the UI
      debugLog(`All endpoints failed for ${logId}, returning simple mock data`);
      return {
        id: logId,
        timestamp: new Date().toISOString(),
        inputType: 'Text',
        duration: 1.5,
        tokenUsage: 0,
        status: 'unknown',
        traceId: logId,
        agent_type: 'unknown',
        input: {
          pageId: '',
          sessionId: '',
          pageContent: ''
        },
        raw_output: `Log with ID ${logId} could not be found in any database`,
        parsed_output: `Log with ID ${logId} could not be found in any database`,
        isMockData: true
      };
    }
  } catch (error) {
    console.error(`Error fetching log ${logId}:`, error);
    // Create a fallback mock log 
    debugLog(`All endpoints failed for ${logId}, returning simple mock data`);
    return {
      id: logId,
      timestamp: new Date().toISOString(),
      inputType: 'Text',
      duration: 1.5,
      tokenUsage: 0,
      status: 'unknown',
      traceId: logId,
      agent_type: 'unknown',
      input: {
        pageId: '',
        sessionId: '',
        pageContent: ''
      },
      raw_output: `Log with ID ${logId} could not be found in any database`,
      parsed_output: `Log with ID ${logId} could not be found in any database`,
      isMockData: true
    };
  }
  },
  /**
   * Format an informative async response message
   */
  formatAsyncResponseMessage(result: any, agentType: string): string {
    const traceId = result.trace_id;
    const estimatedTime = agentType ? this.getEstimatedProcessingTime(agentType) : 20;
    
    return `Your test is being processed asynchronously via Kafka.

Trace ID: ${traceId}
Agent Type: ${agentType}
Status: Processing
Estimated completion time: ${estimatedTime} seconds

The request has been successfully forwarded to the real agent service. Results will be available in the logs when processing is complete.
To check the status manually, go to the Logs page and look for logs with trace ID: ${traceId}`;
  },
  
  /**
   * Get estimated processing time based on agent type
   */
  getEstimatedProcessingTime(agentType: string): number {
    switch (agentType) {
      case 'page-semantic-intent':
      case 'page-role':
        return 15; // Semantic analysis tends to be faster
      case 'customer-intent':
        return 20;
      case 'page-chatbot':
      case 'voice-chatbot':
        return 25; // Chat responses need more contextual understanding
      case 'intent-based-recommender':
        return 30; // Recommendation engines can take longer
      default:
        return 20; // Default estimate
    }
  },
  
  /**
   * Rate an agent execution (provide feedback)
   */
  async rateAgentExecution(traceId: string, agentType: string, rating: 'up' | 'down', reason?: string): Promise<boolean> {
    debugLog(`Rating execution ${traceId} for agent ${agentType}`);
    try {
      const ratingData: AgentRating = {
        traceId,
        agent_type: agentType,
        rating,
        reason,
        created_at: new Date()
      };
      
      const result = await agentApiFixed.rateAgentExecution(ratingData);
      return result.success;
    } catch (error) {
      console.error(`Error rating execution ${traceId}:`, error);
      return false;
    }
  },
  
  /**
   * Get usage statistics for an agent
   */
  async getAgentStats(agentType: string): Promise<AgentStats> {
    debugLog(`Fetching stats for agent ${agentType}`);
    
    // TODO: Implement actual API fetching when available
    
    // For now, return mock data
    return {
      requests: Math.floor(Math.random() * 5000) + 1000,
      averageResponseTime: Math.random() * 3 + 0.5,
      tokensPerRequest: Math.floor(Math.random() * 2000) + 500,
      cumulativeTokens: Math.floor(Math.random() * 10000000) + 1000000,
      accuracy: Math.random() * 0.2 + 0.8,
      customerSatisfaction: Math.random() * 1 + 4,
      trainerSatisfaction: Math.random() * 1 + 4,
      customerReviews: [
        { id: '1', rating: 5, comment: 'Very helpful and accurate', date: new Date().toISOString() },
        { id: '2', rating: 4, comment: 'Good but sometimes slow', date: new Date(Date.now() - 86400000).toISOString() }
      ],
      trainerReviews: [
        { id: '1', rating: 4, comment: 'Performs well on most queries', date: new Date().toISOString() }
      ]
    };
  },
  
  /**
   * Map the legacy agent model to our new Agent model
   */
  mapToNewAgentModel(legacyAgent: any): Agent {
    // Properly determine the agent type using our determineAgentType function
    const agentType = this.determineAgentType(legacyAgent);
    const agentId = legacyAgent._id || agentType;
    
    // Use the determined agent type for lookups
    const mappingInfo = agentTypeToCategory[agentType] || {
      category: 'Research',
      color: 'blue',
      maturity: 'Rookie',
      status: 'available'
    };
    
    const mediaInfo = agentTypeToMedia[agentType] || {
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(agentType)}&background=random`,
      actionVideo: ''
    };
    
    // Special handling for voice agents to ensure they have distinct avatars and videos
    if (agentId === 'voice-chatbot') {
      debugLog('Mapping Voice Chatbot with distinct identity');
    }
    
    return {
      _id: agentId,
      agent_type: agentType,
      name: legacyAgent.name || mappingInfo.displayName || agentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      avatar: legacyAgent.config?.avatar || mediaInfo.avatar,
      actionVideo: legacyAgent.config?.actionVideo || mediaInfo.actionVideo,
      mission: legacyAgent.description || agentTypeToMission[agentType] || `Agent for ${agentType}`,
      maturity: mappingInfo.maturity,
      progress: legacyAgent.progress || Math.floor(Math.random() * 60) + 20, // Random progress between 20-80%
      challenge: legacyAgent.challenge || agentTypeToChallenge[agentType] || 'Improving accuracy and performance',
      category: mappingInfo.category,
      color: mappingInfo.color,
      status: legacyAgent.status || 'active',
      performanceData: legacyAgent.performanceData || (() => {
        const data = [];
        let value = Math.floor(Math.random() * 30) + 30;
        for (let i = 0; i < 12; i++) {
          value += Math.floor(Math.random() * 6) - 1;
          value = Math.max(25, Math.min(95, value));
          data.push(value);
        }
        return data;
      })(),
      performanceChange: legacyAgent.performanceChange || Math.floor(Math.random() * 20) + 5,
      findingsCount: legacyAgent.findingsCount || Math.floor(Math.random() * 40) + 5,
      createdAt: legacyAgent.createdAt || new Date().toISOString(),
      lastActive: legacyAgent.lastActive || new Date().toISOString(),
      source: legacyAgent.source || 'PageMind',
      marketplaceStatus: mappingInfo.status || 'available'
      // agent_type is already defined above at line 1115
    };
  },
  
  /**
   * Map the legacy AgentLog to our new ExecutionLog model
   */
  mapToExecutionLog(agentLog: AgentLog | null): ExecutionLog {
    // Handle case where agentLog is null (API call failed) or missing required fields
    if (!agentLog) {
      console.warn('Received null agentLog in mapToExecutionLog, creating fallback log');
      return {
        id: `log-fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        inputType: 'Unknown',
        duration: Math.random() * 2 + 0.5,
        tokenUsage: Math.floor(Math.random() * 2000) + 500,
        status: 'unknown',
        traceId: `fallback-${Date.now()}`,
        agent_type: 'unknown',
        isMockData: true
      };
    }
    
    // Format timestamp correctly, handling different format types
    let formattedTimestamp: string;
    
    try {
      if (!agentLog.timestamp) {
        // If no timestamp, use current time
        formattedTimestamp = new Date().toISOString();
      } else if (typeof agentLog.timestamp === 'string') {
        // If it's already a string, use it directly or parse if needed
        formattedTimestamp = new Date(agentLog.timestamp).toISOString();
      } else if (agentLog.timestamp instanceof Date) {
        // If it's a Date object, convert to ISO string
        formattedTimestamp = agentLog.timestamp.toISOString();
      } else {
        // For other formats, try to create a Date and then format
        formattedTimestamp = new Date(agentLog.timestamp).toISOString();
      }
    } catch (err) {
      console.error('Error formatting timestamp:', err, 'Timestamp was:', agentLog.timestamp);
      formattedTimestamp = new Date().toISOString();
    }
    
    return {
      id: agentLog._id || agentLog.id || agentLog.trace_id || `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: formattedTimestamp,
      inputType: this.determineInputType(agentLog.input || agentLog.payload),
      duration: agentLog.duration || agentLog.duration_ms ||
                agentLog.timing_breakdown?.total || 
                Math.random() * 2 + 0.5,
      tokenUsage: agentLog.metadata?.tokens_used || Math.floor(Math.random() * 2000) + 500,
      status: (agentLog as any).status || 'success', // Use type assertion for backward compatibility
      traceId: agentLog.traceId || agentLog.trace_id || 'unknown',
      agent_type: agentLog.agent_type || 'unknown',
      
      // If we have more data, pass it along
      input: agentLog.input || agentLog.payload,
      output: agentLog.parsed_output || agentLog.raw_output || agentLog.response,
      metadata: agentLog.metadata,
      raw_output: agentLog.raw_output || agentLog.response,
      parsed_output: agentLog.parsed_output || agentLog.response,
      timing_breakdown: agentLog.timing_breakdown,
      prompt: agentLog.prompt,
      response: agentLog.response
    };
  },
  
  /**
   * Determine the input type from the agent log input
   */
  determineInputType(input: any): string {
    if (!input) return 'Text';
    
    try {
      if (input.imagePath) return 'Image';
      if (input.customerMessage) return 'Chat';
      if (input.pageContent) return 'Page';
    } catch (error) {
      console.warn('Error determining input type:', error);
    }
    
    return 'Text';
  },

  /**
   * Get agent settings
   */
  async getAgentSettings(agentId: string): Promise<AgentSettings | null> {
    debugLog(`Fetching settings for agent ${agentId}`);
    try {
      // Use the dedicated settings endpoint
      const response = await authFetch(`${API_BASE_URL}/agents/${agentId}/settings`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.settings) {
          return {
            prompt: data.settings.prompt || '',
            model: data.settings.model || 'gpt-4o',
            tokenLimit: data.settings.tokenLimit || 4000,
            temperature: data.settings.temperature || 0.7,
            topP: data.settings.topP || 0.9,
            maxLength: data.settings.maxLength || 2000
          };
        }
      }
      
      // Return default settings if API call fails
      return {
        prompt: '',
        model: 'gpt-4o',
        tokenLimit: 4000,
        temperature: 0.7,
        topP: 0.9,
        maxLength: 2000
      };
    } catch (error) {
      console.error(`Error fetching settings for agent ${agentId}:`, error);
      return null;
    }
  },

  /**
   * Save agent settings to the API
   */
  async saveAgentSettings(agentId: string, settings: AgentSettings): Promise<boolean> {
    debugLog(`Saving agent settings for ${agentId}`, settings);
    try {
      // TODO: Implement actual API call to save settings
      const response = await fetch(`${API_BASE_URL}/agents/${agentId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });
      
      return response.ok;
    } catch (error) {
      console.error(`Error saving agent settings for ${agentId}:`, error);
      return false;
    }
  },

  /**
   * Test an agent with given input
   */
  async testAgent(agentType: string, testInput: any): Promise<any> {
    debugLog(`Testing agent ${agentType}`, testInput);
    try {
      // Generate a trace ID for this test
      const traceId = `trace-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // Prepare the test payload
      const payload = {
        agent_type: agentType,
        trace_id: traceId,
        event: {
          payload: testInput
        },
        model_config: {
          model: testInput.model || 'gpt-4o',
          temperature: testInput.temperature || 0.7,
          max_tokens: testInput.maxTokens || 2000
        }
      };
      
      debugLog(`Sending test request to /api/agents-test/test-run`, payload);
      
      const response = await fetch(`${API_BASE_URL}/agents-test/test-run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer test-token'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Test failed with status ${response.status}: ${errorText}`);
        throw new Error(`Test failed: ${response.status}`);
      }
      
      const result = await response.json();
      debugLog(`Test result:`, result);
      
      // Return the result with the trace ID and other relevant data
      return {
        success: result.success,
        traceId: result.trace_id || traceId,
        output: result.result || result.log?.parsed_output,
        prompt: result.log?.prompt,
        timing: {
          total: result.processing_time_ms || result.log?.duration || 0,
          database: 0,
          rag: 0,
          llm: result.processing_time_ms || result.log?.duration || 0
        },
        tokenUsage: result.log?.metadata?.tokens_used || 0,
        raw: result
      };
    } catch (error) {
      console.error(`Error testing agent ${agentType}:`, error);
      throw error;
    }
  }
};